import { Injectable } from '@angular/core';
import { Location } from '@angular/common';
import {
  AccountInfo,
  AuthenticationResult,
  InteractionRequiredAuthError,
  PublicClientApplication,
} from '@azure/msal-browser';
import { MsalService } from '@azure/msal-angular';
import { firstValueFrom } from 'rxjs';
import { AuthConfigService } from './auth-config.service';

export const MICROSOFT_LOGIN_ATTEMPT_KEY = 'pt-microsoft-login-attempt';

@Injectable({ providedIn: 'root' })
export class MicrosoftAuthService {
  private msal?: MsalService;
  private clientInitialization?: Promise<void>;
  private redirectProcessing?: Promise<AuthenticationResult | null>;

  constructor(
    private authConfigService: AuthConfigService,
    private location: Location
  ) {}

  // Lazily configures MSAL once the backend enables Microsoft login.
  private async ensureClient(): Promise<MsalService | undefined> {
    const config = this.authConfigService.config;
    console.debug('[MicrosoftAuthService] ensureClient called', {
      enableMicrosoftLogin: config.enableMicrosoftLogin,
      hasClientId: !!config.clientId,
      hasTenantId: !!config.tenantId,
      existingClient: !!this.msal,
    });

    if (!config.enableMicrosoftLogin) {
      return undefined;
    }

    if (!this.msal) {
      if (!config.clientId || !config.tenantId) {
        return undefined;
      }

      const instance = new PublicClientApplication({
        auth: {
          clientId: config.clientId,
          authority: `https://login.microsoftonline.com/${config.tenantId}`,
          redirectUri: `${window.location.origin}/login`,
          navigateToLoginRequestUrl: false,
        },
        cache: {
          cacheLocation: 'localStorage',
          storeAuthStateInCookie: false,
        },
      });
      console.debug('[MicrosoftAuthService] PublicClientApplication created');
      this.msal = new MsalService(instance, this.location);
      this.clientInitialization = firstValueFrom(this.msal.initialize());
    }

    if (this.clientInitialization) {
      console.debug('[MicrosoftAuthService] Awaiting client initialization');
      await this.clientInitialization;
    }

    return this.msal;
  }

  // Splits user-facing scopes from API scopes to reuse cached tokens.
  private getLoginScopes(): string[] {
    const { scopes } = this.authConfigService.config;
    const nonApiScopes = scopes?.filter((scope) => !scope.startsWith('api://')) ?? [];
    return nonApiScopes.length > 0 ? nonApiScopes : ['openid', 'profile', 'email'];
  }

  private getApiScopes(): string[] {
    const { scopes } = this.authConfigService.config;
    return scopes?.filter((scope) => scope.startsWith('api://')) ?? [];
  }

  // Triggers the Microsoft-hosted login flow.
  async loginRedirect(): Promise<void> {
    this.redirectProcessing = undefined;
    const msal = await this.ensureClient();
    if (!msal) {
      throw new Error('Microsoft login is not configured.');
    }

    const scopes = this.getLoginScopes();
    const account =
      msal.instance.getActiveAccount() ||
      msal.instance.getAllAccounts()[0] ||
      undefined;
    console.debug('[MicrosoftAuthService] loginRedirect', {
      scopes,
      hasAccount: !!account,
    });

    if (account) {
      await firstValueFrom(
        msal.acquireTokenRedirect({
        scopes,
        account,
        })
      );
      return;
    }

    await firstValueFrom(msal.loginRedirect({ scopes }));
  }

  // Processes tokens returned after Microsoft redirects back to /login.
  async handleRedirect(options?: {
    allowSilentFallback?: boolean;
  }): Promise<AuthenticationResult | null> {
    const allowSilentFallback = options?.allowSilentFallback ?? false;
    const msal = await this.ensureClient();
    if (!msal) {
      return null;
    }

    if (!this.redirectProcessing) {
      const scopes = this.getLoginScopes();
      console.debug('[MicrosoftAuthService] handleRedirect start', { scopes });

      this.redirectProcessing = (async () => {
        const redirectResult = await firstValueFrom(
          msal.handleRedirectObservable()
        );
        console.debug('[MicrosoftAuthService] handleRedirectPromise result', {
          hasResult: !!redirectResult,
          hasAccount: !!redirectResult?.account,
        });
        if (redirectResult?.account) {
          msal.instance.setActiveAccount(redirectResult.account);
          console.debug(
            '[MicrosoftAuthService] Active account set from redirect result',
            {
              username: redirectResult.account.username,
            }
          );
          return redirectResult;
        }

        console.debug(
          '[MicrosoftAuthService] Silent fallback disabled, skipping cached account token acquisition'
        );
        return null;
      })();
    }

    const result = await this.redirectProcessing;
    this.redirectProcessing = undefined;
    return result ?? null;
  }

  // Grabs API-scoped access tokens once the user is logged in with MSAL.
  async acquireApiToken(): Promise<AuthenticationResult | null> {
    const msal = await this.ensureClient();
    if (!msal) {
      return null;
    }

    const scopes = this.getApiScopes();
    if (scopes.length === 0) {
      return null;
    }

    const instance = msal.instance;
    const account = instance.getActiveAccount() || instance.getAllAccounts()[0];
    if (!account) {
      console.debug('[MicrosoftAuthService] acquireApiToken no active account');
      return null;
    }

    try {
      const result = await firstValueFrom(
        msal.acquireTokenSilent({
          scopes,
          account,
        })
      );
      console.debug('[MicrosoftAuthService] acquireApiToken silent success', {
        expiresOn: result.expiresOn?.toISOString?.() ?? null,
      });
      return result;
    } catch (error) {
      if (error instanceof InteractionRequiredAuthError) {
        console.debug('[MicrosoftAuthService] acquireApiToken interaction required');
        await firstValueFrom(
          msal.acquireTokenRedirect({
            scopes,
            account,
          })
        );
        return null;
      }

      console.debug('[MicrosoftAuthService] acquireApiToken unexpected error', error);
      throw error;
    }
  }

  // Initiates the Microsoft logout flow to clear MSAL browser state.
  async logout(): Promise<void> {
    const msal = await this.ensureClient();
    if (!msal) {
      return;
    }

    await firstValueFrom(msal.logout());
  }
}
